/**
 * 连接复用器 — 多路复用 + 通道隔离 + 流控
 */

export interface Channel {
  id: string
  name: string
  priority: number
  maxConcurrent: number
  currentCount: number
  totalSent: number
}

export interface MultiplexedMessage {
  channelId: string
  messageId: string
  payload: any
  timestamp: number
}

export class ConnectionMultiplexer {
  private channels = new Map<string, Channel>()
  private messageQueue = new Map<string, MultiplexedMessage[]>()
  private idCounter = 0
  private maxQueuePerChannel = 1000

  /** 创建通道 */
  createChannel(name: string, options?: { priority?: number; maxConcurrent?: number }): string {
    const id = `ch_${++this.idCounter}`
    this.channels.set(id, { id, name, priority: options?.priority ?? 5, maxConcurrent: options?.maxConcurrent ?? 10, currentCount: 0, totalSent: 0 })
    this.messageQueue.set(id, [])
    return id
  }

  /** 关闭通道 */
  closeChannel(id: string): void {
    this.channels.delete(id)
    this.messageQueue.delete(id)
  }

  /** 发送消息 */
  send(channelId: string, payload: any): string | null {
    const channel = this.channels.get(channelId)
    if (!channel) return null
    if (channel.currentCount >= channel.maxConcurrent) {
      const queue = this.messageQueue.get(channelId)!
      if (queue.length >= this.maxQueuePerChannel) return null
      const msgId = `msg_${++this.idCounter}`
      queue.push({ channelId, messageId: msgId, payload, timestamp: Date.now() })
      return msgId
    }
    channel.currentCount++
    channel.totalSent++
    return `msg_${++this.idCounter}`
  }

  /** 完成消息处理 */
  complete(channelId: string): MultiplexedMessage | null {
    const channel = this.channels.get(channelId)
    if (!channel) return null
    channel.currentCount = Math.max(0, channel.currentCount - 1)
    const queue = this.messageQueue.get(channelId)
    if (queue && queue.length > 0) {
      const next = queue.shift()!
      channel.currentCount++
      channel.totalSent++
      return next
    }
    return null
  }

  /** 获取通道 */
  getChannel(id: string): Channel | null {
    return this.channels.get(id) || null
  }

  /** 获取所有通道 */
  listChannels(): Channel[] {
    return [...this.channels.values()].sort((a, b) => b.priority - a.priority)
  }

  /** 获取通道队列长度 */
  getQueueSize(channelId: string): number {
    return this.messageQueue.get(channelId)?.length ?? 0
  }

  /** 获取统计 */
  getStats(): { channels: number; totalQueued: number; totalSent: number } {
    let totalQueued = 0, totalSent = 0
    for (const q of this.messageQueue.values()) totalQueued += q.length
    for (const c of this.channels.values()) totalSent += c.totalSent
    return { channels: this.channels.size, totalQueued, totalSent }
  }
}
